import { useMemo, type ReactNode } from 'react'
import { api } from '../api/public'
import { useApi } from './useApi'
import { PlayerDirectoryContext, isPublicId, type PlayerDirectory } from './playerPaths'

/**
 * Loads the roster once, for the whole public tree, and turns it into the two
 * maps every player link needs.
 *
 * Seven rows, one request, cached by `useApi` at the layout — and `Player.tsx`
 * already fetched the roster to render opponents, so for that view this costs
 * nothing at all.
 *
 * See `playerPaths.ts` for why the slug is a browser concern and never an
 * identifier.
 */
export function PlayerPathProvider({ children }: { children: ReactNode }) {
  const roster = useApi((s) => api.players(s), [])
  const state = roster.state
  const data = roster.state === 'ready' ? roster.data : undefined

  const value = useMemo<PlayerDirectory>(() => {
    const slugById = new Map<string, string>()
    const idBySlug = new Map<string, string>()
    for (const p of data ?? []) {
      if (p.slug) {
        slugById.set(p.public_id, p.slug)
        idBySlug.set(p.slug, p.public_id)
      }
    }
    return {
      pathFor: (publicId) => `/players/${slugById.get(publicId) ?? publicId}`,
      slugFor: (publicId) => slugById.get(publicId),
      resolve: (param) => {
        if (!param) return undefined
        if (isPublicId(param)) return param
        return idBySlug.get(param)
      },
      // An ERROR counts as loaded. Otherwise a roster that failed to load
      // leaves every player page spinning forever rather than saying so, and
      // Constitution IV is explicit that a blank page is the worst degradation
      // because it cannot be told from "nothing happened yet".
      loaded: state !== 'loading',
    }
  }, [data, state])

  return (
    <PlayerDirectoryContext.Provider value={value}>{children}</PlayerDirectoryContext.Provider>
  )
}
